import { Injectable } from '@angular/core';
import { Observable, throwError} from 'rxjs';
import { ProtobufService } from './protobuf.service';

import {
  PlaceAssetRequest,
  PlaceAssetResponse,
  GetAssetResponse
} from '@proto/odoo/asset';
import {filter, map} from "rxjs/operators";
import { HttpClient, HttpEventType } from "@angular/common/http";

@Injectable({
  providedIn: 'root'
})
export class AssetService {
  private readonly baseUrl = '/services/asset';

  constructor(private protobufService: ProtobufService, private httpClient: HttpClient) {}

  /**
   * Place an asset - get presigned upload URL
   * POST /services/asset
   * 
   * Response codes:
   * - 200 OK: Success
   * - 500 Internal Server Error: Something went wrong
   */
  placeAsset(fileName: string, mimeType: string): Observable<PlaceAssetResponse> {
    // Browsers don't assign a MIME type to files with unregistered extensions (e.g. .log).
    // The backend rejects an empty mime_type with 400, so fall back to the generic binary type.
    const effectiveMimeType = mimeType || 'application/octet-stream';
    const request = PlaceAssetRequest.create({
      fileMetaData: {
        meta: {
          $case: 'assetMetaV1',
          assetMetaV1: {
            fileName,
            mimeType: effectiveMimeType
          }
        }
      }
    });

    return this.protobufService.put(
      this.baseUrl,
      PlaceAssetRequest.encode(request),
    ).pipe(map(r => {
      if (r) {
        return PlaceAssetResponse.decode(r)
      }
    }));
  }

  /**
   * Get asset - get presigned download URL and metadata
   * GET /services/asset/{assetKey}
   * 
   * Response codes:
   * - 200 OK: Success
   * - 404 Not Found: Asset key does not exist
   * - 500 Internal Server Error: Something went wrong
   */
  getAsset(assetKey: string): Observable<GetAssetResponse> {
    return this.protobufService.get(
      `${this.baseUrl}/${assetKey}`,
    ).pipe(map(r => {
      if (r) {
        return GetAssetResponse.decode(r)
      }
    }));
  }

  /**
   * Upload file to S3 using presigned URL
   * This is a direct HTTP PUT to S3, not through the protobuf service
   */
  public upload(url: string, file: File): Observable<any> {
    if (!file) {
      return throwError(() => new Error('file_upload_error'));
    }

    // Send File directly - S3 can handle File objects
    // Filter for the final Response event only, so firstValueFrom waits for completion
    return this.httpClient.put(url, file, { 
      reportProgress: false, 
      observe: 'events' 
    }).pipe(
      filter(event => event.type === HttpEventType.Response)
    );
  }

  public download(url: string, fileName: string): Observable<File> {
    if (!url) {
      return throwError(() => new Error('file_download_error'));
    }

    return this.httpClient.get(url, { responseType: 'blob' }).pipe(
      map(blob => new File([blob], fileName, { type: blob.type }))
    );
  }
}
